export interface ServiceItem {
  id: string;
  slug: string;
  title: string;
  shortDesc: string;
  fullDesc: string;
  symptoms: string[];
  treatments: string[];
  faqs: { question: string; answer: string }[];
  metaTitle: string;
  metaDesc: string;
}

export const PRACTICE_INFO = {
  name: "Wasatch Foot & Ankle Center",
  legalName: "Wasatch Foot & Ankle Center, LLC",
  doctor: "Spence D. Harper, DPM",
  doctorTitle: "Board-Certified Podiatric Physician & Surgeon",
  credentials: "DPM, DABFAS",
  phone: "435-657-0329",
  officePhone: "(435) 657-0329",
  fax: "801-274-9064",
  contactPerson: "Spence Harper",
  address: {
    street: "190 N. Main Street",
    city: "Heber City",
    state: "UT",
    zip: "84032",
    country: "US"
  },
  hours: [
    { days: "Monday", hours: "8:00 AM – 5:00 PM" },
    { days: "Tuesday", hours: "8:00 AM – 5:00 PM" },
    { days: "Wednesday", hours: "Closed for Surgery" },
    { days: "Thursday", hours: "8:00 AM – 5:00 PM" },
    { days: "Friday", hours: "Closed (Scheduled Procedures)" },
    { days: "Saturday – Sunday", hours: "Closed" }
  ],
  heroVideo: "/manus-storage/mountain-people-walking-hero_8346f76b.mp4",
  doctorPhoto: "/manus-storage/dr-spence-harper_029434bc.png",
  receptionPhoto: "/manus-storage/reception-main_cb5cd9eb.webp",
  receptionSecondaryPhoto: "/manus-storage/reception-seating_63068361.webp",
  treatmentRoomPhoto: "/manus-storage/treatment-room_24264148.webp",
  swiftBannerPhoto: "/manus-storage/swift-device-banner_dde06407.jpeg",
  swiftDevicePhoto: "/manus-storage/swift-device-hand_738f8b3e.jpg",
  heberValleyPhoto: "/manus-storage/heber-valley_fb3484dd.jpg",
  services: [
    {
      id: "swift-therapy",
      slug: "swift-microwave-therapy",
      title: "Swift Microwave Therapy for Plantar Warts",
      shortDesc: "FDA-cleared microwave technology that delivers brief, targeted energy to plantar warts and may help stimulate the body's immune response.",
      fullDesc: "Swift is an FDA-cleared podiatric technology designed for plantar warts that have not responded to other options. A specialized contact probe delivers brief, targeted microwave energy to the lesion. Patients may feel a short, sharp burst of heat during each application. The treatment does not require an incision or routine bandaging, and Dr. Harper will explain the expected response and number of visits for your situation.",
      symptoms: [
        "Painful 'pebble in shoe' sensation while walking or exercising",
        "Tenderness when squeezing the sides of the lesion",
        "Warts that have resisted over-the-counter freezing or acid kits",
        "Rough, circular skin spots with tiny dark pinpoint dots"
      ],
      treatments: [
        "Brief in-office applications with a simple 'sock off, sock on' visit",
        "No incision and no routine post-visit bandaging",
        "No messy home creams or daily dressings between monthly visits",
        "Many patients return to normal activity after treatment, as directed by the clinic"
      ],
      faqs: [
        {
          question: "How does Swift microwave therapy work?",
          answer: "Swift delivers brief, controlled microwave energy through a specialized contact probe. The treatment is intended to stimulate a local immune response to the plantar wart."
        },
        {
          question: "Does the treatment hurt?",
          answer: "Patients may feel a brief, sharp burst of heat with each application. Dr. Harper will discuss what to expect before treatment."
        },
        {
          question: "How many sessions are typically required?",
          answer: "The number of visits varies by the wart and the individual response. Dr. Harper will recommend a schedule after examining the area."
        }
      ],
      metaTitle: "Swift Microwave Therapy for Warts Heber City UT | Wasatch Foot & Ankle",
      metaDesc: "Learn about FDA-cleared Swift microwave treatment for plantar warts in Heber City, UT with Dr. Spence Harper."
    },
    {
      id: "surgical-corrections",
      slug: "surgical-foot-ankle-corrections",
      title: "Advanced Foot & Ankle Surgery",
      shortDesc: "Board-certified surgical precision for complex deformities, fractures, structural misalignments, and chronic joint degeneration.",
      fullDesc: "As a board-certified foot and ankle surgeon certified by the American Board of Foot and Ankle Surgery (ABFAS), Dr. Spence Harper brings over 25 years of surgical leadership to Wasatch County. While conservative non-invasive methods are always pursued first, surgical correction provides long-term alignment, joint preservation, and pain relief when structural trauma, arthritis, or severe biomechanical collapse occurs.",
      symptoms: [
        "Severe bone spurs, joint stiffness, or deformity that impedes walking",
        "Chronic instability, recurring ankle sprains, or traumatic fractures",
        "Structural foot deformities unresponsive to conservative orthotics",
        "Intense bone-on-bone arthritic discomfort in the midfoot or hindfoot"
      ],
      treatments: [
        "Minimally invasive and arthroscopic joint corrections",
        "Reconstructive tendon repair and joint stabilization",
        "Rigid fixation and deformity realignments",
        "Comprehensive postoperative physical rehabilitation coordination"
      ],
      faqs: [
        {
          question: "When is surgical intervention necessary?",
          answer: "Surgery is considered when non-surgical measures (custom orthotics, bracing, injections, or therapy) fail to relieve disabling pain or restore stable mobility."
        },
        {
          question: "Where are surgical procedures performed?",
          answer: "Dr. Harper operates at accredited local surgical facilities including Cottonwood Surgical Center, Heber Valley Medical Center, and Intermountain facilities."
        }
      ],
      metaTitle: "Foot & Ankle Surgery Heber City UT | Dr. Spence Harper DPM",
      metaDesc: "Board-certified foot and ankle surgeon Dr. Spence Harper offers reconstructive surgery, fracture repair, and deformity corrections in Heber City, UT."
    },
    {
      id: "bunion-correction",
      slug: "bunion-correction",
      title: "Bunion Correction & Hallux Valgus Relief",
      shortDesc: "Targeted correction of painful bunions to restore anatomical alignment, relieve friction, and return you to active mountain lifestyle.",
      fullDesc: "A bunion (hallux valgus) is a progressive progressive bone deformity of the first metatarsophalangeal joint at the base of the big toe. Over time, genetic foot structure, abnormal gait pressures, or footwear can force the big toe to lean inward toward second toe. Dr. Harper has led extensive clinical trials in bunion pain management and provides both conservative offloading and precise modern surgical corrections.",
      symptoms: [
        "Visible bump or protrusion on the outside border of the big toe joint",
        "Persistent redness, swelling, or burning over the prominent bone",
        "Difficulty finding comfortable athletic shoes or ski boots",
        "Restricted, painful big toe motion when pushing off during strides"
      ],
      treatments: [
        "Digital gait evaluation and customized prescription biomechanical orthotics",
        "Protective medical padding, footwear modifications, and anti-inflammatory therapy",
        "Modern anatomic osteotomy to realign the deviated first metatarsal bone",
        "Gentle soft-tissue rebalancing to prevent recurrence"
      ],
      faqs: [
        {
          question: "Can bunions resolve on their own without treatment?",
          answer: "No. Bunions are progressive structural bone misalignments. While non-surgical care manages swelling and arrests progression, surgery is required to physically straighten the bone."
        },
        {
          question: "What is recovery like after modern bunion surgery?",
          answer: "With contemporary surgical stabilization techniques, many patients begin protected weight-bearing in a specialized postoperative boot much sooner than with older traditional methods."
        }
      ],
      metaTitle: "Bunion Surgery & Treatment Heber City UT | Wasatch Foot & Ankle",
      metaDesc: "Relieve bunion pain in Heber City, UT with Dr. Spence Harper. Personalized conservative treatments and advanced surgical bunion corrections."
    },
    {
      id: "heel-pain-plantar-fasciitis",
      slug: "heel-pain-plantar-fasciitis",
      title: "Heel Pain & Plantar Fasciitis Therapy",
      shortDesc: "Evidence-informed evaluation, stretching, footwear guidance, and orthotic support for chronic heel pain and plantar fasciitis.",
      fullDesc: "Plantar fasciitis is a common cause of heel pain involving irritation of the thick band that supports the foot arch. Dr. Harper evaluates walking mechanics, activity, footwear, and contributing factors before recommending a tailored plan that may include stretching, orthotic support, activity changes, and other appropriate treatments.",
      symptoms: [
        "Sharp, stabbing heel pain during your very first morning steps",
        "Pain returning after prolonged sitting followed by standing",
        "Ache worsening after intense hiking, running, or standing on hard surfaces",
        "Tenderness along the bottom inside edge of the heel bone"
      ],
      treatments: [
        "Custom medical-grade arch orthotics to redistribute plantar tension",
        "Focused plantar fascia stretching and night splint stabilization",
        "Footwear and activity guidance, with other treatments when appropriate"
      ],
      faqs: [
        {
          question: "What causes plantar fasciitis and heel pain?",
          answer: "Heel pain can have several causes, including plantar fascia irritation, changes in activity, footwear, calf tightness, and foot mechanics. An exam helps identify the most likely contributors."
        },
        {
          question: "How quickly do patients experience relief?",
          answer: "Improvement varies by cause and duration. Dr. Harper will explain a realistic timeline and the steps that may help."
        }
      ],
      metaTitle: "Heel Pain & Plantar Fasciitis Heber City UT | Wasatch Foot & Ankle",
      metaDesc: "Get a personalized evaluation for heel pain and plantar fasciitis in Heber City, UT with Dr. Spence Harper."
    },
    {
      id: "ingrown-toenails",
      slug: "ingrown-toenails",
      title: "Ingrown Toenail Care & Permanent Relief",
      shortDesc: "Gentle in-office procedures to relieve painful ingrown nail edges, inflammation, and pressure.",
      fullDesc: "An ingrown toenail occurs when the curved border of the nail plate penetrates the adjacent skin folds, triggering inflammation, throbbing, and sometimes infection. Dr. Harper provides in-office relief with local anesthesia and, when appropriate, a partial nail procedure intended to reduce the chance of recurrence.",
      symptoms: [
        "Red, warm, swollen flesh surrounding the nail edge",
        "Sharp throbbing exacerbated by bedsheets, shoes, or light contact",
        "Drainage, pus, or overgrown hyper-granulation tissue",
        "Inability to participate in soccer, hiking, or running"
      ],
      treatments: [
        "Same-day emergency comfort and pressure relief",
        "Partial nail avulsion under local anesthesia",
        "Matrix treatment when appropriate to reduce recurrence",
        "Antiseptic soothing soaks and rapid healing guidance"
      ],
      faqs: [
        {
          question: "Does the entire toenail have to be removed?",
          answer: "Rarely. In almost all cases, only the small ingrown sliver (roughly 1 to 2 millimeters) is excised, leaving the remainder of your natural nail cosmetically intact."
        },
        {
          question: "How long before I can resume normal shoe wear?",
          answer: "Most patients return to school, work, and comfortable shoes within 24 to 48 hours following the gentle in-office procedure."
        }
      ],
      metaTitle: "Ingrown Toenail Treatment Heber City UT | Dr. Spence Harper",
      metaDesc: "Fast, permanent ingrown toenail relief in Heber City, UT. Gentle in-office treatments by Dr. Spence Harper. Call Wasatch Foot & Ankle Center."
    },
    {
      id: "neuromas",
      slug: "mortons-neuroma-treatment",
      title: "Morton's Neuroma Treatment",
      shortDesc: "Targeted decompression, offloading, and therapy to stop burning nerve pain, numbness, and tingling in the ball of your foot.",
      fullDesc: "Morton's neuroma is a painful condition characterized by the thickening and irritation of the tissue surrounding one of the digital nerves leading to your toes, usually between the third and fourth metatarsals. Patients frequently describe feeling as though they are walking on a bunched-up sock or hot marble. Dr. Harper provides accurate diagnostic tests and non-surgical nerve calming protocols.",
      symptoms: [
        "Burning sensation in the ball of the foot that radiates into the toes",
        "Sensation of standing on a stone, pebble, or bunched sock",
        "Numbness, tingling, or cramping in the third and fourth toes",
        "Urgent desire to take off shoes and rub the forefoot"
      ],
      treatments: [
        "Metatarsal pads and biomechanical offloading orthotics",
        "Footwear widening and toe box pressure relief",
        "Targeted anti-inflammatory injections to reduce nerve swelling",
        "Surgical nerve decompression or excision for refractory cases"
      ],
      faqs: [
        {
          question: "Can wide shoes help cure a neuroma?",
          answer: "Wider footwear with a generous toe box relieves lateral compression on the digital nerve, dramatically reducing symptoms in early stages."
        },
        {
          question: "What if conservative treatments don't relieve the burning?",
          answer: "If non-invasive therapy does not resolve discomfort, Dr. Harper performs precise outpatient neurolysis or neuroma excision to eliminate the irritated nerve segment."
        }
      ],
      metaTitle: "Morton's Neuroma Treatment Heber City UT | Ball of Foot Pain",
      metaDesc: "Stop ball-of-foot burning and numbness with Morton's neuroma care in Heber City, UT. Board-certified podiatric solutions by Dr. Spence Harper."
    }
  ],
  testimonials: [
    {
      quote: "Dr. Harper is great! He takes time to listen and provides understandable answers. He is an awesome dr.",
      author: "Twila A.",
      location: "Heber City, UT",
      rating: 5
    },
    {
      quote: "Very professional, kind, attentive and informative visit. We made a treatment plan with the best information available.",
      author: "Chris M.",
      location: "Midway, UT",
      rating: 5
    },
    {
      quote: "Dr. Harper was very patient and made sure I fully understood all my options! He is very knowledgeable and empathetic. Highly recommend!",
      author: "Constance W.",
      location: "Park City, UT",
      rating: 5
    },
    {
      quote: "I was in lots of pain and his office got me in quickly. Dr. Harper was very friendly and great at explaining what was going on with my foot. The office staff was friendly and efficient!",
      author: "Gladys A.",
      location: "Heber Valley, UT",
      rating: 5
    },
    {
      quote: "Dr. Spencer Harper is amazing! He is very good at explaining everything and making you feel comfortable. The office staff is amazing, clean, and welcoming. Best around!",
      author: "Marc W.",
      location: "Wasatch County, UT",
      rating: 5
    }
  ],
  marketingKeywords: [
    "Wound Care",
    "Foot Ulcers",
    "Diabetic Foot Care",
    "Ingrown Toenails",
    "Nail Fungus",
    "Fractures",
    "Swift Wart Treatment",
    "New Patients",
    "Local Provider",
    "Heber Podiatrist",
    "Second Opinions",
    "Bunion",
    "Lapiplasty"
  ],
  insurances: [
    "Aetna",
    "Blue Cross Blue Shield",
    "Cigna",
    "Intermountain Healthcare (SelectHealth)",
    "Regence BlueCross BlueShield",
    "PEHP",
    "DMBA",
    "Medicaid",
    "TRICARE",
    "UnitedHealthcare",
    "Medicare & Supplemental Plans"
  ]
};
